import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdminSubscriptionPlan,
  SubscriptionPlanFormData,
  SubscriptionPlanOptions,
  PlanLocaleCode,
  createSubscriptionPlan,
  defaultPlanForm,
  deleteSubscriptionPlan,
  formToPayload,
  getPlanDisplayLabel,
  getSubscriptionPlanOptions,
  getSubscriptionSettings,
  listSubscriptionPlans,
  planToForm,
  updateSubscriptionPlan,
  updateSubscriptionSettings,
  validateSubscriptionPlanForm,
} from '../services/subscriptionPlansAdminApi';
import { ContentPageLayout } from '../components/Shared';

const SubscriptionPlansAdminPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [plans, setPlans] = useState<AdminSubscriptionPlan[]>([]);
  const [activeGateway, setActiveGateway] = useState<string>('polar');
  const [options, setOptions] = useState<SubscriptionPlanOptions | null>(null);
  const [dialog, setDialog] = useState<{ open: boolean; editing?: AdminSubscriptionPlan }>({ open: false });
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [form, setForm] = useState<SubscriptionPlanFormData>(defaultPlanForm());
  const [localeTab, setLocaleTab] = useState<PlanLocaleCode>('en');
  const [trialEnabled, setTrialEnabled] = useState(true);
  const [trialDays, setTrialDays] = useState(15);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [plansRes, optionsRes, settingsRes] = await Promise.all([
        listSubscriptionPlans(),
        getSubscriptionPlanOptions(),
        getSubscriptionSettings(),
      ]);
      setPlans(plansRes.plans || []);
      setActiveGateway(plansRes.activeGateway || 'polar');
      setOptions(optionsRes.options);
      if (settingsRes.settings) {
        setTrialEnabled(settingsRes.settings.registrationTrialEnabled);
        setTrialDays(settingsRes.settings.registrationTrialDays);
      }
    } catch (e: any) {
      if (!opts?.silent) {
        setError(e?.message || t('subscriptionPlans.errors.loadFailed'));
      }
    } finally {
      if (!opts?.silent) {
        setLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const saveTrialSettings = async () => {
    const days = Number(trialDays);
    if (!Number.isInteger(days) || days < 1 || days > 90) {
      setError(t('subscriptionPlans.trialSettings.daysInvalid'));
      return;
    }
    setSettingsSaving(true);
    setError(null);
    try {
      await updateSubscriptionSettings({
        registrationTrialEnabled: trialEnabled,
        registrationTrialDays: days,
      });
      setMsg(t('subscriptionPlans.trialSettings.saved'));
    } catch (e: any) {
      setError(e?.message || t('subscriptionPlans.trialSettings.saveFailed'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const closeDialog = () => {
    setDialog({ open: false, editing: undefined });
    setDialogError(null);
    setForm(defaultPlanForm());
    setLocaleTab('en');
  };

  const openCreate = () => {
    setForm(defaultPlanForm());
    setLocaleTab('en');
    setDialogError(null);
    setDialog({ open: true });
  };

  const openEdit = (plan: AdminSubscriptionPlan) => {
    setForm(planToForm(plan));
    setLocaleTab('en');
    setDialogError(null);
    setDialog({ open: true, editing: plan });
  };

  const handleNameChange = (name: SubscriptionPlanFormData['name']) => {
    setForm((prev) => ({
      ...defaultPlanForm(name),
      polarProductId: prev.polarProductId,
    }));
  };

  const updateLocaleField = (
    locale: PlanLocaleCode,
    field: 'displayName' | 'description' | 'features',
    value: string,
  ) => {
    setForm((prev) => ({
      ...prev,
      locales: {
        ...prev.locales,
        [locale]: {
          ...prev.locales[locale],
          [field]: value,
        },
      },
    }));
  };

  const toggleGateway = (gateway: 'polar' | 'paypal') => {
    setForm((prev) => {
      const set = new Set(prev.allowedGateways);
      if (set.has(gateway)) set.delete(gateway);
      else set.add(gateway);
      return { ...prev, allowedGateways: [...set] as ('polar' | 'paypal')[] };
    });
  };

  const handleSave = async () => {
    const validationKey = validateSubscriptionPlanForm(form);
    if (validationKey) {
      setDialogError(t(`subscriptionPlans.validation.${validationKey}`));
      return;
    }

    setSaving(true);
    setMsg(null);
    setError(null);
    setDialogError(null);
    try {
      const payload = formToPayload(form);
      if (dialog.editing) {
        await updateSubscriptionPlan(dialog.editing._id, payload);
        setMsg(t('subscriptionPlans.saved'));
      } else {
        await createSubscriptionPlan(payload);
        setMsg(t('subscriptionPlans.created'));
      }
      closeDialog();
      await load({ silent: true });
    } catch (e: any) {
      const message = e?.message || t('subscriptionPlans.errors.saveFailed');
      setDialogError(message);
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan: AdminSubscriptionPlan) => {
    if (!window.confirm(t('subscriptionPlans.confirmDelete', { name: getPlanDisplayLabel(plan, 'en') }))) return;
    setError(null);
    try {
      await deleteSubscriptionPlan(plan._id);
      setMsg(t('subscriptionPlans.deleted'));
      await load();
    } catch (e: any) {
      setError(e?.message || t('subscriptionPlans.errors.deleteFailed'));
    }
  };

  if (loading) {
    return (
      <ContentPageLayout>
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
      </ContentPageLayout>
    );
  }

  return (
    <ContentPageLayout>
    <Box>
      <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
            {t('subscriptionPlans.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('subscriptionPlans.subtitle')}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {t('subscriptionPlans.addPlan')}
        </Button>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert> : null}
      {msg ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t('subscriptionPlans.trialSettings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('subscriptionPlans.trialSettings.subtitle')}
        </Typography>
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={trialEnabled}
                onChange={(e) => setTrialEnabled(e.target.checked)}
              />
            }
            label={t('subscriptionPlans.trialSettings.enabled')}
          />
          <TextField
            type="number"
            size="small"
            label={t('subscriptionPlans.trialSettings.days')}
            value={trialDays}
            onChange={(e) => setTrialDays(Number(e.target.value))}
            inputProps={{ min: 1, max: 90 }}
            disabled={!trialEnabled}
            sx={{ width: 140 }}
            helperText={t('subscriptionPlans.trialSettings.daysHelp')}
          />
          <Button
            variant="outlined"
            onClick={saveTrialSettings}
            disabled={settingsSaving}
          >
            {settingsSaving ? t('subscriptionPlans.saving') : t('save')}
          </Button>
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('subscriptionPlans.activeGateway', { gateway: activeGateway.toUpperCase() })}{' '}
        <Button component={RouterLink} to="../payment-gateway" size="small">
          {t('subscriptionPlans.changeGateway')}
        </Button>
      </Alert>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('subscriptionPlans.columns.name')}</TableCell>
              <TableCell>{t('subscriptionPlans.columns.displayName')}</TableCell>
              <TableCell>{t('subscriptionPlans.columns.price')}</TableCell>
              <TableCell>{t('subscriptionPlans.columns.gateways')}</TableCell>
              <TableCell>{t('subscriptionPlans.columns.active')}</TableCell>
              <TableCell>{t('subscriptionPlans.columns.sort')}</TableCell>
              <TableCell align="right">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('noData')}
                </TableCell>
              </TableRow>
            ) : (
              plans.map((plan) => (
                <TableRow key={plan._id} hover>
                  <TableCell>{plan.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {getPlanDisplayLabel(plan, 'en')}
                    </Typography>
                    {plan.locales?.fr?.displayName ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        FR: {plan.locales.fr.displayName}
                      </Typography>
                    ) : null}
                    {plan.locales?.en?.description || plan.description ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {plan.locales?.en?.description || plan.description}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {plan.price.toFixed(2)} {plan.currency}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {plan.billingCycle}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(plan.allowedGateways || []).length ? (
                      plan.allowedGateways.map((g) => (
                        <Chip key={g} label={g.toUpperCase()} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>{plan.isActive ? t('yes') : t('no')}</TableCell>
                  <TableCell>{plan.sortOrder}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(plan)} aria-label={t('edit')}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(plan)} aria-label={t('delete')}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: 'text.primary.main' }}>
          {dialog.editing ? t('subscriptionPlans.editPlan') : t('subscriptionPlans.addPlan')}
        </DialogTitle>
        <DialogContent dividers>
          {dialogError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          ) : null}
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
            <TextField
              select
              label={t('subscriptionPlans.fields.name')}
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value as SubscriptionPlanFormData['name'])}
              disabled={!!dialog.editing}
              fullWidth
              sx={{ gridColumn: '1 / -1' }}
            >
              {(options?.planNames || ['Monthly', 'Quarterly', 'Yearly', 'Trial', 'Test']).map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </TextField>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('subscriptionPlans.fields.localizedContent')}
              </Typography>
              <Tabs value={localeTab} onChange={(_, value) => setLocaleTab(value)} sx={{ mb: 2 }}>
                <Tab label={t('subscriptionPlans.localeTabs.en')} value="en" />
                <Tab label={t('subscriptionPlans.localeTabs.fr')} value="fr" />
              </Tabs>
              <Box display="grid" gridTemplateColumns={{ xs: '1fr' }} gap={2}>
                <TextField
                  label={t('subscriptionPlans.fields.displayName')}
                  value={form.locales[localeTab].displayName}
                  onChange={(e) => updateLocaleField(localeTab, 'displayName', e.target.value)}
                  fullWidth
                  required={localeTab === 'en'}
                />
                <TextField
                  label={t('subscriptionPlans.fields.description')}
                  value={form.locales[localeTab].description}
                  onChange={(e) => updateLocaleField(localeTab, 'description', e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <TextField
                  label={t('subscriptionPlans.fields.features')}
                  value={form.locales[localeTab].features}
                  onChange={(e) => updateLocaleField(localeTab, 'features', e.target.value)}
                  fullWidth
                  multiline
                  minRows={4}
                  helperText={t('subscriptionPlans.fields.featuresHelp')}
                />
              </Box>
            </Box>
            <TextField
              label={t('subscriptionPlans.fields.price')}
              type="number"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              select
              label={t('subscriptionPlans.fields.currency')}
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              fullWidth
            >
              {(options?.currencies || ['EUR']).map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('subscriptionPlans.fields.durationDays')}
              value={form.durationDays}
              onChange={(e) => setForm({ ...form, durationDays: Number(e.target.value) })}
              fullWidth
            >
              {(options?.durationDays || [1, 30, 90, 365]).map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('subscriptionPlans.fields.billingCycle')}
              value={form.billingCycle}
              onChange={(e) => setForm({ ...form, billingCycle: e.target.value })}
              fullWidth
            >
              {(options?.billingCycles || ['daily', 'monthly', 'quarterly', 'yearly']).map((cycle) => (
                <MenuItem key={cycle} value={cycle}>{cycle}</MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('subscriptionPlans.fields.polarProductId')}
              value={form.polarProductId}
              onChange={(e) => setForm({ ...form, polarProductId: e.target.value })}
              fullWidth
              helperText={t('subscriptionPlans.fields.polarProductIdHelp')}
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label={t('subscriptionPlans.fields.tags')}
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              fullWidth
              helperText={t('subscriptionPlans.fields.tagsHelp')}
            />
            <TextField
              label={t('subscriptionPlans.fields.sortOrder')}
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
              fullWidth
            />
            <TextField
              label={t('subscriptionPlans.fields.discountPercentage')}
              type="number"
              value={form.discountPercentage}
              onChange={(e) => setForm({ ...form, discountPercentage: Number(e.target.value) })}
              fullWidth
              inputProps={{ min: 0, max: 100 }}
            />
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('subscriptionPlans.fields.allowedGateways')}
              </Typography>
              {form.name === 'Trial' || form.name === 'Teacher' ? (
                <Typography variant="body2" color="text.secondary">
                  {form.name === 'Teacher'
                    ? 'Teacher plans are assigned automatically and cannot be sold at checkout.'
                    : t('subscriptionPlans.trialNoGateways')}
                </Typography>
              ) : (
                <Box display="flex" gap={2}>
                  {(options?.gateways || ['polar', 'paypal']).map((gateway) => (
                    <FormControlLabel
                      key={gateway}
                      control={
                        <Checkbox
                          checked={form.allowedGateways.includes(gateway)}
                          onChange={() => toggleGateway(gateway)}
                        />
                      }
                      label={gateway.toUpperCase()}
                    />
                  ))}
                </Box>
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isRecurring}
                  onChange={(e) => setForm({ ...form, isRecurring: e.target.checked })}
                />
              }
              label={t('subscriptionPlans.fields.isRecurring')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label={t('subscriptionPlans.fields.isActive')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isPopular}
                  onChange={(e) => setForm({ ...form, isPopular: e.target.checked })}
                />
              }
              label={t('subscriptionPlans.fields.isPopular')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t('cancel')}</Button>
          <Button type="button" variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? t('subscriptionPlans.saving') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ContentPageLayout>
  );
};

export default SubscriptionPlansAdminPage;
